import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Copy,
  Heart,
  MapPin,
} from "lucide-react";
import { LikedStore } from "../../../../Redux/store_like/types";
import {
  formatStuffDate,
  formatStuffLongDate,
  mapLikedStoreToRecord,
  LikedStoreRecord,
} from "../stuffAdapters";

interface LikedStoresTableProps {
  stores: LikedStore[];
  startIndex?: number;
  onUnlike: (targetStoreId: string) => void;
}

const LikedStoresTable: React.FC<LikedStoresTableProps> = ({
  stores,
  startIndex = 0,
  onUnlike,
}) => {
  const navigate = useNavigate();
  const [copiedOfferId, setCopiedOfferId] = useState<string | null>(null);
  const records = stores.map((store, i) =>
    mapLikedStoreToRecord(store, startIndex + i),
  );

  const openDirections = (store: LikedStoreRecord) => {
    const query =
      store.latitude && store.longitude
        ? `${store.latitude},${store.longitude}`
        : encodeURIComponent(`${store.storeName}, ${store.address}`);
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${query}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const copyOffer = async (store: LikedStoreRecord) => {
    try {
      await navigator.clipboard.writeText(store.offerCode);
      setCopiedOfferId(store.id);
      window.setTimeout(() => setCopiedOfferId(null), 1300);
    } catch {
      setCopiedOfferId(null);
    }
  };

  return (
    <>
      <div className="liked-stores-desktop-table-wrap">
        <table className="liked-stores-desktop-table">
          <thead>
            <tr>
              <th>S.NO</th>
              <th>DATE</th>
              <th>STATUS</th>
              <th>STORE DETAILS</th>
              <th>OFFERS</th>
            </tr>
          </thead>
          <tbody>
            {records.length > 0 ? (
              records.map((store) => (
                <tr key={store.id}>
                  <td className="liked-stores-serial-cell">
                    {store.serialNo}
                  </td>
                  <td className="liked-stores-date-cell">
                    <strong>{formatStuffDate(store.date)}</strong>
                    <small>{store.time}</small>
                  </td>
                  <td>
                    <div className="liked-stores-status-actions">
                      <button
                        type="button"
                        className="liked active"
                        disabled
                      >
                        <Heart size={13} strokeWidth={2} />
                        Liked
                      </button>
                      <button
                        type="button"
                        className="unliked"
                        onClick={() => onUnlike(store.targetStoreId)}
                      >
                        Un-Liked
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="liked-stores-store-cell">
                      <div className="liked-stores-table-image">
                        <img src={store.image} alt="" />
                      </div>
                      <div className="liked-stores-store-cell-copy">
                        <h3>{store.storeName}</h3>
                        <p>
                          <b>Address :</b> {store.address}
                        </p>
                        <p>
                          <b>Contact Number :</b> {store.contactNumber}
                        </p>
                        <button
                          type="button"
                          className="liked-stores-directions-button"
                          onClick={() => openDirections(store)}
                        >
                          <MapPin size={14} strokeWidth={2} />
                          Directions
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="liked-stores-offer-cell">
                    <span>Offer Code</span>
                    <strong>{store.offerCode}</strong>
                    <button
                      type="button"
                      onClick={() => copyOffer(store)}
                    >
                      <Copy size={14} strokeWidth={2} />
                      {copiedOfferId === store.id ? "Copied" : "Copy"}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="liked-stores-desktop-empty" colSpan={5}>
                  No liked stores found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="liked-stores-mobile-content">
        <div className="liked-stores-mobile-list">
          {records.length > 0 ? (
            records.map((store) => (
              <button
                type="button"
                className="liked-stores-mobile-card"
                key={store.id}
                onClick={() => navigate("/LikedStoreDetails")}
              >
                <span className="liked-stores-mobile-serial">
                  {store.serialNo}
                </span>
                <div className="liked-stores-mobile-image">
                  <img src={store.image} alt="" />
                </div>
                <div className="liked-stores-mobile-copy">
                  <div className="liked-stores-mobile-topline">
                    <span className="liked-stores-mobile-status liked-stores-mobile-status-liked">
                      Liked
                    </span>
                    <span>
                      <CalendarDays size={13} strokeWidth={2} />
                      {formatStuffLongDate(store.date)}
                    </span>
                  </div>
                  <h2>{store.storeName}</h2>
                  <p>{store.address}</p>
                </div>
                <ChevronRight
                  className="liked-stores-mobile-chevron"
                  size={20}
                  strokeWidth={2}
                />
              </button>
            ))
          ) : (
            <div className="liked-stores-mobile-empty">
              <Heart size={32} strokeWidth={1.8} />
              <strong>No stores found</strong>
              <span>Try another search or date range.</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LikedStoresTable;
